import { getRestorationZoneDemandRestoredEventsJsonData } from "api/dataVisualisation/restorationZone/restorationZoneDemandRestored";
import Button from "components/components/Button/Button";
import SummaryTile from "components/dataVisualization/dataPageComponents/SummaryTile/SummaryTile";
import {
  Header,
  SummaryTable,
  ValueCell,
} from "components/dataVisualization/dataPageComponents/SummaryTile/commonStyle";
import { useChartFocussedDateContext } from "contexts/ChartFocussedDateContext";
import { RestorationZoneDemandRestoredModel } from "models/restorationZone/restorationZoneDemandRestored";
import React, { useEffect, useMemo, useState } from "react";
import { DateFormat, formatDateTimeString } from "utils/dateHelpers";

const NUMBER_OF_EVENTS = 3;

const RestorationZoneDemandRestoredSummaryTile: React.FC = () => {
  const { setFocussedDate: setFocus } = useChartFocussedDateContext();
  const [summaryTileData, setSummaryTileData] =
    useState<RestorationZoneDemandRestoredModel[]>();
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    getRestorationZoneDemandRestoredEventsJsonData(NUMBER_OF_EVENTS)
      .then((response) => {
        if (response && response.data.length === 0) {
          setSummaryTileData([]);
          return;
        }

        setSummaryTileData(response.data);
      })
      .catch((err) => {
        setError(err);
        console.error("Failed to load restoration events:", err);
      });
  }, []);

  const uniqueDates = useMemo(() => {
    if (!summaryTileData) {
      return undefined;
    }
    const uniqueDateTimes = Array.from(
      new Set(summaryTileData.map((event) => event.submissionDateTime))
    );
    return uniqueDateTimes.map((dateString) => new Date(dateString));
  }, [summaryTileData]);

  return (
    <SummaryTile>
      <SummaryTable $tableLayout="fixed">
        <thead>
          <tr>
            <Header colSpan={4}>Latest restoration events (UTC)</Header>
          </tr>
        </thead>
        <tbody>
          {error ? (
            <tr>
              <td
                colSpan={4}
                className="has-text-danger"
                data-test-id="events-error-message"
              >
                Error loading data
              </td>
            </tr>
          ) : uniqueDates && uniqueDates.length > 0 ? (
            uniqueDates.map((submissionDateTime, index) => (
              <tr key={submissionDateTime.toUTCString()}>
                <ValueCell
                  colSpan={2}
                  className="left-align"
                  data-test-id={`event-date-${index}`}
                >
                  {formatDateTimeString(
                    submissionDateTime,
                    DateFormat.DateOnly
                  )}
                </ValueCell>
                <td colSpan={1} data-test-id={`event-time-${index}`}>
                  {formatDateTimeString(
                    submissionDateTime,
                    DateFormat.TimeOnly
                  )}
                </td>
                <td
                  colSpan={1}
                  className="right-align"
                  data-test-id={`event-view-${index}`}
                >
                  <Button
                    onClick={(): void => setFocus(submissionDateTime)}
                    buttonText="View"
                    className="link-inline"
                  />
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} data-test-id="no-events-message">
                No restoration events received.
              </td>
            </tr>
          )}
        </tbody>
      </SummaryTable>
    </SummaryTile>
  );
};

export default RestorationZoneDemandRestoredSummaryTile;
